import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import CustomTextInput from '../../../components/CustomTextInput';
import SingleDatePicker from '../../../components/form/SingleDatePicker';
import dayjs from 'dayjs';
import { map } from 'lodash';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

export default function EntityDetailsForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  readOnly = true,
  isVerified = true,
  isLoading = false,
}) {
  const theme = useTheme();
  const { businessProfiles } = useTranslatedMessages();
  const t = businessProfiles?.entity_details ?? {};
  const labels = t.labels ?? {};
  const actions = businessProfiles?.actions ?? {};

  const {
    register,
    control,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    // Skip validation for now as all fields are disabled and getting auto filled from the API response
    mode: 'onSubmit',
    defaultValues: {
      legal_type: initialData?.legal_type || '',
      entity_name_en: initialData?.entity_name_en || '',
      entity_name_ar: initialData?.entity_name_ar || '',
      vat_trn: initialData?.vat_trn || '',
      effective_date: dayjs(initialData?.effective_date).isValid() ? dayjs(initialData?.effective_date) : null,
      submission_date: initialData?.submission_date || dayjs(),
      declaration: false,
    },
  });

  useEffect(() => {
    map(apiErrors, (value, key) => {
      setError(key, { message: value, type: 'string' });
    });
  }, [setError, apiErrors]);

  const handleFormSubmit = async (data) => {
    const submitData = {
      ...initialData,
      submission_date: dayjs(data.submission_date).format('YYYY-MM-DD'),
    };
    onSubmit(submitData);
  };

  const declaration = useWatch({ control, name: 'declaration' });
  return (
    <Paper elevation={0} sx={{ p: 1 }}>
      <Typography variant="h6" mb={2}>
        {t.title ?? 'Entity Details'}
        <Divider></Divider>
      </Typography>
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="legal-type-input"
              name="legal_type"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.legal_type ?? 'Legal Type'}
              helperText={undefined}
              endAdornment={undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="entity-legal-name-english-input"
              name="entity_name_en"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.entity_name_en ?? 'Entity Legal Name in English'}
              helperText={undefined}
              endAdornment={undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="entity-legal-name-arabic-input"
              name="entity_name_ar"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.entity_name_ar ?? 'Entity Legal Name in Arabic'}
              helperText={undefined}
              endAdornment={undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="vat-trn-input"
              name="vat_trn"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.vat_trn ?? 'VAT TRN'}
              inputProps={{ maxLength: 15 }}
              helperText={undefined}
              endAdornment={undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SingleDatePicker
              data-testid="effective-date-input"
              name="effective_date"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.effective_date ?? 'Effective Date of Registration / Joining VAT Group'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SingleDatePicker
              data-testid="date-of-submission-input"
              name="submission_date"
              control={control}
              errors={errors}
              readOnly={readOnly}
              disabled={isVerified}
              label={labels.submission_date ?? 'Date of Submission'}
            />
          </Grid>
        </Grid>

        <Box>
          <Controller
            name="declaration"
            control={control}
            render={({ field }) => (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label={
                    labels.declaration ??
                    'I declare that all information provided is true, accurate and complete to the best of my knowledge and belief'
                  }
                />
                {errors.declaration && (
                  <FormHelperText error sx={{ ml: 0 }}>
                    {errors.declaration.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} mt={3}>
          <Button
            data-testid="entity-details-submit"
            key={'submit'}
            variant="contained"
            onClick={handleSubmit(handleFormSubmit)}
            disabled={!isVerified || isLoading || !declaration}
          >
            {isLoading ? (actions.submitting ?? 'Submitting...') : (actions.submit ?? 'Submit')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
